import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@store';
import { IconLoader } from '@ui/src';
import { OrganizationType } from '../types';
import { GeneralInfo } from '../../individual/types';
import { CorporateGeneralInfo } from '../../corporate/types';
import Card from '../../../components/common/card/card';
import useStatistics from './hooks/use-statistics';
import { setType } from '../store/slices/statistics';
import BaseStatistics from '../../../components/common/statistics/base-statistics';
import Currencies from '../../../components/common/statistics/currencies';
import ProfitByCustomer from '../../../components/common/statistics/profit-by-customer';

import cn from './styles.module.css';

const Statistics = ({ customerType }: { customerType: OrganizationType }) => {
  const { baseStatistic, profitByCustomer, type } = useAppSelector(
    (state) => state.statistic,
  );
  const { currencies, currenciesList } = useAppSelector((state) => state.root);
  const { customersTransactionTypes, baseCurrency } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const {
    baseInformation: corporateBaseInformation,
    balances: corporateBalances,
    totalBalance: corporateTotalBalance,
  } = useAppSelector((state) => state.corporateCustomers.info.general);
  const {
    baseInformation: individualBaseInformation,
    balances: individualBalances,
    totalBalance: individualTotalBalance,
  } = useAppSelector((state) => state.individualCustomers.info.general);

  const dispatch = useAppDispatch();
  const { customerId } = useParams();

  const {
    isLoading,
    isFetching,
    baseStatisticDates,
    setBaseStatisticsDate,
    getBaseStatisticsAction,
    profitIsLoading,
    profitIsFetching,
    profitByCustomerDates,
    setProfitByCustomerDate,
    getProfitByCustomerAction,
  } = useStatistics({
    customerId,
    baseStatistic,
    profitByCustomer,
  });

  const currentBalances =
    customerType === OrganizationType.INDIVIDUAL
      ? individualBalances
      : corporateBalances;

  const currentTotalBalance =
    customerType === OrganizationType.INDIVIDUAL
      ? individualTotalBalance
      : corporateTotalBalance;

  const baseInformation =
    customerType === OrganizationType.INDIVIDUAL
      ? individualBaseInformation
      : corporateBaseInformation;

  useEffect(() => {
    let title: string;

    const companyName = localStorage.getItem('companyName');

    if (customerType === OrganizationType.INDIVIDUAL) {
      if (
        (baseInformation as Partial<GeneralInfo>)?.firstName ||
        (baseInformation as Partial<GeneralInfo>)?.lastName
      ) {
        title = `${(baseInformation as Partial<GeneralInfo>)?.firstName} ${(baseInformation as Partial<GeneralInfo>)?.lastName}: Statistics`;
      } else {
        title = 'Statistics';
      }
    } else if ((baseInformation as Partial<CorporateGeneralInfo>)?.name) {
      title = `${(baseInformation as Partial<CorporateGeneralInfo>)?.name}: Statistics`;
    } else {
      title = 'Statistics';
    }

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [baseInformation]);

  return (
    <div>
      <Card
        title="Base statistics"
        isLoading={isLoading}
        className={cn.baseStatisticCard}
      >
        {isFetching && type === 'base' && (
          <div className={cn.baseStatisticLoader}>
            <IconLoader />
          </div>
        )}

        <BaseStatistics
          baseStatistic={baseStatistic}
          customersTransactionTypes={customersTransactionTypes}
          baseCurrency={baseCurrency}
          currenciesList={currencies}
          baseStatisticDates={baseStatisticDates}
          setBaseStatisticsDate={setBaseStatisticsDate}
          onChangeBaseDates={(dates) => {
            dispatch(setType('base'));

            let datesQuery = `transferDateFrom=${dates[0].toISOString()}`;

            if (dates.length > 1) {
              datesQuery = `${datesQuery}&transferDateTo=${dates[1].toISOString()}`;
            }

            getBaseStatisticsAction(datesQuery);
          }}
          onResetBaseDates={() => {
            dispatch(setType('base'));

            getBaseStatisticsAction();
          }}
        />
      </Card>
      <Currencies
        isLoading={Boolean(!currentBalances)}
        currencies={currentTotalBalance?.currency}
        balance={currentBalances}
        currenciesList={currenciesList}
        dictionariesCurrencies={currencies}
      />
      <Card
        title="Profit by Customer"
        className={cn.baseStatisticCard}
        isLoading={isLoading || profitIsLoading}
      >
        {profitIsFetching && type === 'profit' && (
          <div className={cn.baseStatisticLoader}>
            <IconLoader />
          </div>
        )}
        <ProfitByCustomer
          profitByCustomer={profitByCustomer}
          baseCurrency={baseCurrency}
          onChangeProfitDates={(dates) => {
            dispatch(setType('profit'));

            let datesQuery = `transferDateFrom=${dates[0].toISOString()}`;

            if (dates.length > 1) {
              datesQuery = `${datesQuery}&transferDateTo=${dates[1].toISOString()}`;
            }

            getProfitByCustomerAction(datesQuery);
          }}
          onResetProfitDates={() => {
            dispatch(setType('profit'));
            getProfitByCustomerAction();
          }}
          currenciesList={currencies}
          profitByCustomerDates={profitByCustomerDates}
          setProfitByCustomerDate={setProfitByCustomerDate}
        />
      </Card>
    </div>
  );
};

export default Statistics;
